import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import accountServices from "app/services/account-services";

const CODE_LENGTH = 6;
const POLL_EVERY_MS = 3000;
const MAX_POLLS = 10;
const PENDING_STATUSES = ["queued", "sending", "sent", "accepted", "scheduled"];

const errorText = (error, fallback) => error?.response?.data?.errors?.join(". ") || fallback;

// Verifica el teléfono del usuario: al abrirse le manda un código por WhatsApp (plantilla de autenticación, con el botón
// «Copiar código») y el usuario lo pega aquí. iPhone no autocompleta códigos que llegan por WhatsApp, así que el campo
// acepta el código pegado con espacios y verifica solo al tener los 6 dígitos. Avisa si WhatsApp no pudo entregarlo.
const WhatsappVerificationDialog = ({ open, phone, onClose, onVerified }) => {
    const [code, setCode] = useState("");
    const [sent, setSent] = useState(null);
    const [sending, setSending] = useState(false);
    const [sendError, setSendError] = useState(null);
    const [verifying, setVerifying] = useState(false);
    const [codeError, setCodeError] = useState(null);
    const [now, setNow] = useState(Date.now());
    const polls = useRef(0);

    const send = async () => {
        setSending(true);
        setSendError(null);
        setCodeError(null);
        try {
            const data = await accountServices.sendPhoneCode();
            polls.current = 0;
            setSent(data);
        } catch (error) {
            // Si hace poco ya se envió uno (por ejemplo, al reabrir el diálogo), ese sigue sirviendo.
            const latest = await accountServices.getPhoneCode().catch(() => null);
            if (latest?.pending) setSent(latest);
            else setSendError(errorText(error, "No se pudo enviar el código. Intenta de nuevo."));
        } finally {
            setSending(false);
        }
    };

    useEffect(() => {
        if (!open) return undefined;
        setCode("");
        setSent(null);
        setCodeError(null);
        send();
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timer);
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    // Mientras WhatsApp no confirme la entrega, se pregunta cada pocos segundos (un rato, no para siempre).
    useEffect(() => {
        if (!open || !sent || !PENDING_STATUSES.includes(sent.delivery_status) || polls.current >= MAX_POLLS) return undefined;
        const timer = setTimeout(() => {
            polls.current += 1;
            accountServices.getPhoneCode().then(setSent).catch(() => {});
        }, POLL_EVERY_MS);
        return () => clearTimeout(timer);
    }, [open, sent]);

    const verify = async (value) => {
        setVerifying(true);
        setCodeError(null);
        try {
            const account = await accountServices.verifyPhone(value);
            onVerified(account);
        } catch (error) {
            setCode("");
            setCodeError(errorText(error, "No se pudo verificar el código."));
        } finally {
            setVerifying(false);
        }
    };

    const changeCode = (event) => {
        const digits = event.target.value.replace(/\D/g, "").slice(0, CODE_LENGTH);
        setCode(digits);
        if (digits.length === CODE_LENGTH && !verifying) verify(digits);
    };

    const waitSeconds = sent?.resend_at ? Math.max(0, Math.ceil((new Date(sent.resend_at).getTime() - now) / 1000)) : 0;
    const shownPhone = sent?.phone || phone;
    const delivered = ["delivered", "read"].includes(sent?.delivery_status);

    let status = null;
    if (sending) status = "Enviando el código…";
    else if (sent?.delivery_status === "logged") status = "Modo desarrollo: el código quedó en el log del backend.";
    else if (delivered) status = "WhatsApp lo entregó.";
    else if (sent && !sent.delivery_error) status = "Enviado. Puede tardar unos segundos en llegar.";

    return (
        <Dialog open={open} onClose={verifying ? undefined : onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Verificar tu WhatsApp</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    <Typography variant="body2">
                        Te enviamos un código por WhatsApp al <strong>{shownPhone}</strong>. En WhatsApp toca «Copiar código», vuelve aquí y pégalo.
                    </Typography>
                    {status && (
                        <Stack direction="row" spacing={0.75} alignItems="center">
                            {delivered && <CheckCircleOutlineIcon color="success" fontSize="small" />}
                            <Typography variant="body2" color="text.secondary">{status}</Typography>
                        </Stack>
                    )}
                    {!!sent?.delivery_error && <Alert severity="error">{sent.delivery_error}</Alert>}
                    {!!sendError && <Alert severity="warning">{sendError}</Alert>}
                    <TextField
                        autoFocus
                        fullWidth
                        label="Código de 6 dígitos"
                        value={code}
                        onChange={changeCode}
                        disabled={verifying}
                        error={!!codeError}
                        helperText={codeError || " "}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                            inputMode: "numeric",
                            autoComplete: "one-time-code",
                            pattern: "[0-9]*",
                            style: { fontSize: 22, letterSpacing: "0.4em", textAlign: "center" },
                        }}
                    />
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 1.5 }}>
                <Button onClick={send} disabled={sending || verifying || waitSeconds > 0} sx={{ mr: "auto" }}>
                    {waitSeconds > 0 ? `Reenviar en ${waitSeconds} s` : "Reenviar código"}
                </Button>
                <Button onClick={onClose} disabled={verifying}>Cancelar</Button>
                <LoadingButton variant="contained" loading={verifying} disabled={code.length !== CODE_LENGTH} onClick={() => verify(code)}>
                    Verificar
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default WhatsappVerificationDialog;
